// src/firebase/registrations.js

import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { formatLocalDateTime } from '../utils/recurringUtils';

/**
 * Normalize an instance date key to ensure consistent format with .000 milliseconds
 * This prevents duplicate instances when keys like "2026-03-10T19:30:00" and "2026-03-10T19:30:00.000" are used
 * @param {string} instanceDate - Instance date string
 * @returns {string|null} - Normalized instance date with .000 milliseconds, or null
 */
const normalizeInstanceDate = (instanceDate) => {
  if (!instanceDate) return null;

  // Parse the date and re-format it to ensure consistent .000 milliseconds
  const date = new Date(instanceDate);
  if (isNaN(date.getTime())) return instanceDate; // Return as-is if invalid

  return formatLocalDateTime(date);
};

/**
 * Register a user for a tournament
 * @param {string} tournamentId - Tournament ID
 * @param {object} user - User object with id, displayName, photoURL, etc.
 * @param {string|null} instanceDate - Instance date (ISO string) for recurring tournaments
 * @param {string|null} partnerName - Partner name for doubles/scotch doubles tournaments
 * @returns {object} - { success: boolean, error: Error|null, isFull: boolean }
 */
export const registerForTournament = async (tournamentId, user, instanceDate = null, partnerName = null) => {
  try {
    // Normalize instanceDate to ensure consistent key format (with .000 milliseconds)
    const normalizedInstanceDate = normalizeInstanceDate(instanceDate);

    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const tournamentDoc = await getDoc(tournamentRef);

    if (!tournamentDoc.exists()) {
      return { success: false, error: new Error('Tournament not found'), isFull: false };
    }

    const tournamentData = tournamentDoc.data();
    const registrations = tournamentData.registrations || { registeredUsers: [], maxCapacity: null };

    // For recurring tournaments, filter registrations for this specific instance
    // Check both normalized and original keys for backwards compatibility
    let instanceRegistrations = registrations.registeredUsers || [];
    if (normalizedInstanceDate) {
      instanceRegistrations = instanceRegistrations.filter(reg =>
        reg.instanceDate === normalizedInstanceDate || reg.instanceDate === instanceDate
      );
    }

    // Check if user is already registered for this instance
    // Check both normalized and original keys for backwards compatibility
    const isAlreadyRegistered = normalizedInstanceDate
      ? registrations.registeredUsers?.some(reg =>
          reg.userId === user.uid &&
          (reg.instanceDate === normalizedInstanceDate || reg.instanceDate === instanceDate)
        )
      : registrations.registeredUsers?.some(reg => reg.userId === user.uid && !reg.instanceDate);

    if (isAlreadyRegistered) {
      return { success: false, error: new Error('Already registered'), isFull: false };
    }

    // Check capacity for this specific instance
    if (registrations.maxCapacity) {
      const currentCount = instanceRegistrations.length;
      if (currentCount >= registrations.maxCapacity) {
        return { success: false, error: new Error('Tournament is full'), isFull: true };
      }
    }

    // Create registration object
    // Note: Use new Date() instead of serverTimestamp() because serverTimestamp()
    // cannot be used inside arrayUnion()
    const registration = {
      userId: user.uid,
      displayName: user.displayName || 'Anonymous',
      photoURL: user.photoURL || null,
      registeredAt: new Date(),
      status: 'confirmed'
    };

    // Add instance date for recurring tournaments (use normalized version)
    if (normalizedInstanceDate) {
      registration.instanceDate = normalizedInstanceDate;
    }

    // Add user's ratings if available from their profile
    if (user.ratings) {
      registration.ratings = user.ratings;
    }

    // Add partner name for doubles/scotch doubles tournaments
    if (partnerName && partnerName.trim()) {
      registration.partnerName = partnerName.trim();
    }

    // Build the playerUserIds mapping for this user
    // For doubles, use the team name (displayName & partnerName)
    const displayName = user.displayName || 'Anonymous';
    const playerName = partnerName ? `${displayName} & ${partnerName.trim()}` : displayName;

    // Prepare the update object
    const updateData = {
      'registrations.registeredUsers': arrayUnion(registration),
      updatedAt: serverTimestamp()
    };

    // playerUserIds (player name -> uid) for stats linkage.
    // - Non-recurring: stored at the tournament root (read by the stats recorder
    //   and mirrored by the director's sync).
    // - Recurring: the occurrence now lives in a subcollection the registrant
    //   cannot write, and writing `instances.<date>` on the parent is rejected by
    //   the tightened security rules. We DON'T write playerUserIds here — the
    //   registration object already carries `userId` + `displayName`, and the stats
    //   recorder derives name->uid from `registrations` for the occurrence.
    if (!normalizedInstanceDate) {
      const existingPlayerUserIds = tournamentData.playerUserIds || {};
      updateData.playerUserIds = {
        ...existingPlayerUserIds,
        [playerName]: user.uid
      };
    }

    // Update tournament with new registration and playerUserIds mapping
    await updateDoc(tournamentRef, updateData);

    return { success: true, error: null, isFull: false };
  } catch (error) {
    console.error('Error registering for tournament:', error);
    return { success: false, error, isFull: false };
  }
};

/**
 * Unregister a user from a tournament
 * @param {string} tournamentId - Tournament ID
 * @param {string} userId - User ID
 * @param {string|null} instanceDate - Instance date (ISO string) for recurring tournaments
 * @returns {object} - { success: boolean, error: Error|null }
 */
export const unregisterFromTournament = async (tournamentId, userId, instanceDate = null) => {
  try {
    // Normalize instanceDate to ensure consistent key format
    const normalizedInstanceDate = normalizeInstanceDate(instanceDate);

    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const tournamentDoc = await getDoc(tournamentRef);

    if (!tournamentDoc.exists()) {
      return { success: false, error: new Error('Tournament not found') };
    }

    const tournamentData = tournamentDoc.data();
    const registrations = tournamentData.registrations || { registeredUsers: [] };

    // Find the user's registration for this specific instance
    // Check both normalized and original keys for backwards compatibility
    const userRegistration = normalizedInstanceDate
      ? registrations.registeredUsers?.find(reg =>
          reg.userId === userId &&
          (reg.instanceDate === normalizedInstanceDate || reg.instanceDate === instanceDate)
        )
      : registrations.registeredUsers?.find(reg => reg.userId === userId && !reg.instanceDate);

    if (!userRegistration) {
      return { success: false, error: new Error('Not registered') };
    }

    // Build the player name to remove from playerUserIds
    const displayName = userRegistration.displayName || 'Anonymous';
    const playerName = userRegistration.partnerName
      ? `${displayName} & ${userRegistration.partnerName}`
      : displayName;

    // Prepare update to remove registration
    const updateData = {
      'registrations.registeredUsers': arrayRemove(userRegistration),
      updatedAt: serverTimestamp()
    };

    // Also remove from playerUserIds (non-recurring only — recurring occurrences
    // don't store playerUserIds on the parent; see registerForTournament).
    if (!normalizedInstanceDate) {
      const existingPlayerUserIds = { ...(tournamentData.playerUserIds || {}) };
      delete existingPlayerUserIds[playerName];
      updateData.playerUserIds = existingPlayerUserIds;
    }

    // Remove the registration and update playerUserIds
    await updateDoc(tournamentRef, updateData);

    return { success: true, error: null };
  } catch (error) {
    console.error('Error unregistering from tournament:', error);
    return { success: false, error };
  }
};

/**
 * Get registrations for a tournament
 * @param {string} tournamentId - Tournament ID
 * @returns {object} - { registrations: object, error: Error|null }
 */
export const getTournamentRegistrations = async (tournamentId) => {
  try {
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const tournamentDoc = await getDoc(tournamentRef);

    if (!tournamentDoc.exists()) {
      return { registrations: null, error: new Error('Tournament not found') };
    }

    const tournamentData = tournamentDoc.data();
    const registrations = tournamentData.registrations || {
      registeredUsers: [],
      maxCapacity: null
    };

    return { registrations, error: null };
  } catch (error) {
    console.error('Error getting tournament registrations:', error);
    return { registrations: null, error };
  }
};

/**
 * Check if a user is registered for a tournament
 * @param {string} tournamentId - Tournament ID
 * @param {string} userId - User ID
 * @param {string|null} instanceDate - Instance date (ISO string) for recurring tournaments
 * @returns {object} - { isRegistered: boolean, error: Error|null }
 */
export const isUserRegistered = async (tournamentId, userId, instanceDate = null) => {
  try {
    // Normalize instanceDate to ensure consistent key format
    const normalizedInstanceDate = normalizeInstanceDate(instanceDate);

    const { registrations, error } = await getTournamentRegistrations(tournamentId);

    if (error) {
      return { isRegistered: false, error };
    }

    // Check both normalized and original keys for backwards compatibility
    const isRegistered = normalizedInstanceDate
      ? registrations.registeredUsers?.some(reg =>
          reg.userId === userId &&
          (reg.instanceDate === normalizedInstanceDate || reg.instanceDate === instanceDate)
        ) || false
      : registrations.registeredUsers?.some(reg => reg.userId === userId && !reg.instanceDate) || false;

    return { isRegistered, error: null };
  } catch (error) {
    console.error('Error checking registration status:', error);
    return { isRegistered: false, error };
  }
};

/**
 * Update max capacity for a tournament
 * @param {string} tournamentId - Tournament ID
 * @param {number|null} maxCapacity - Max capacity (null for unlimited)
 * @returns {object} - { success: boolean, error: Error|null }
 */
export const updateTournamentCapacity = async (tournamentId, maxCapacity) => {
  try {
    const tournamentRef = doc(db, 'tournaments', tournamentId);

    await updateDoc(tournamentRef, {
      'registrations.maxCapacity': maxCapacity,
      updatedAt: serverTimestamp()
    });

    return { success: true, error: null };
  } catch (error) {
    console.error('Error updating tournament capacity:', error);
    return { success: false, error };
  }
};
